import { persist } from 'zustand/middleware';

import { initialSorting } from '@constants/tableSorting';
import { troubleshootingHoldOption } from '@constants/troubleshootingHoldOption';

import { createStore } from '@store/createStore';

const filterInitialState = {
  search: '',
  maxTimeCreated: undefined,
  minTimeCreated: undefined,
  urgency: { value: 'all', label: 'All' },
  type: { value: 'all', label: 'All' },
  cartRange: {
    minCart: undefined,
    maxCart: undefined,
  },
  states: [0, 2, 3, 5, 6, 7, 9, 10, troubleshootingHoldOption.id],
  reportedFailure: false,
  isSmallWidthAdditionalFilterChanged: false,
  isAdditionalFilterChanged: false,
  sorting: { ...initialSorting },
};

const useEngagementFilterStore = createStore(
  persist(
    (set) => ({
      ...filterInitialState,
      showFullTotalStatistics: false,
      actions: {
        setEngagementFilterInitialState: () => set({ ...filterInitialState }),
        setEngagementFilterSearchValue: (search) => set({ search }),
        setEngagementFilterSortingValue: (sorting) => set({ sorting }),
        setEngagementFilterTimeCreatedRangeValue: ({ maxTimeCreated, minTimeCreated }) =>
          set({ minTimeCreated, maxTimeCreated, isSmallWidthAdditionalFilterChanged: true }),
        setEngagementFilterUrgencyValue: (urgency) => set({ urgency }),
        setEngagementFilterTypeValue: (type) => set({ type }),
        setEngagementFilterCartRangeValue: (cartRange) => set({ cartRange, isAdditionalFilterChanged: true }),
        setEngagementFilterStatesValue: (states) => set({ states }),
        setEngagementFilterReportFailure: (reportedFailure) => set({ reportedFailure }),
        setEngagementFilterShowFullTotalStatistics: (showFullTotalStatistics) =>
          set({ showFullTotalStatistics }),
      },
    }),
    {
      version: 3,
      name: 'engagement-filters',
      partialize: (state) =>
        Object.fromEntries(Object.entries(state).filter(([key]) => key !== 'actions' && key !== 'sorting')),
    },
  ),
  'EngagementFilterStore',
);

export const useEngagementFilterActions = () => useEngagementFilterStore((state) => state.actions);

export const selectEngagementFilterState = () =>
  useEngagementFilterStore((state) => ({
    maxTimeCreated: state.maxTimeCreated,
    minTimeCreated: state.minTimeCreated,
    urgency: state.urgency.value,
    type: state.type.value,
    states: state.states,
    reportedFailure: state.reportedFailure,
  }));

export const selectEngagementFilterSearchValue = () => useEngagementFilterStore((state) => state.search);
export const selectEngagementFilterSortingValue = () => useEngagementFilterStore((state) => state.sorting);
export const selectEngagementFilterTimeCreatedRangeValue = () =>
  useEngagementFilterStore((state) => ({
    maxTimeCreated: state.maxTimeCreated,
    minTimeCreated: state.minTimeCreated,
  }));
export const selectEngagementFilterUrgencyValue = () => useEngagementFilterStore((state) => state.urgency);
export const selectEngagementFilterTypeValue = () => useEngagementFilterStore((state) => state.type);
export const selectEngagementFilterCartRangeValue = () =>
  useEngagementFilterStore((state) => state.cartRange);
export const selectEngagementFilterStatesValue = () => useEngagementFilterStore((state) => state.states);
export const selectEngagementFilterReportFailure = () =>
  useEngagementFilterStore((state) => state.reportedFailure);
export const selectEngagementFilterIsChangedValue = () =>
  useEngagementFilterStore(
    (state) => state.isAdditionalFilterChanged || state.isSmallWidthAdditionalFilterChanged,
  );
export const selectEngagementAdditionalFilterIsChangedValue = () =>
  useEngagementFilterStore((state) => state.isAdditionalFilterChanged);
export const selectEngagementSmallWidthAdditionalFilterIsChangedValue = () =>
  useEngagementFilterStore((state) => state.isSmallWidthAdditionalFilterChanged);
export const selectEngagementFilterShowFullTotalStatistics = () =>
  useEngagementFilterStore((state) => state.showFullTotalStatistics);
